import { useEffect, useState } from "react";
import { colors, fontSizes } from "../styles/theme";
import { getCookieConsent, setCookieConsent } from "../lib/cookieConsent";

/* Cookie consent banner (#2255 — legal floor for EU/CA customers).
 *
 * Renders fixed-bottom on every page until the user makes a choice. Choices:
 *   - Accept all   → analytics:true
 *   - Essential only → analytics:false
 *   - Learn more   → /cookies
 *
 * The banner reappears if CONSENT_VERSION in lib/cookieConsent.ts is bumped
 * (forces re-prompt after a policy change). Keyboard-accessible: Tab focus +
 * Enter to activate.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(getCookieConsent() === null);
  }, []);

  // Re-show on a custom event so other parts of the app (e.g. the Cookie page's
  // "Reset cookie consent" button) can re-trigger the banner without a reload.
  useEffect(() => {
    const onReset = () => setVisible(getCookieConsent() === null);
    window.addEventListener("2hives:cookie-consent-reset", onReset);
    return () => window.removeEventListener("2hives:cookie-consent-reset", onReset);
  }, []);

  if (!visible) return null;

  function acceptAll() {
    setCookieConsent(true);
    setVisible(false);
  }

  function essentialOnly() {
    setCookieConsent(false);
    setVisible(false);
  }

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      style={{
        position: "fixed",
        left: 0, right: 0, bottom: 0,
        background: "rgba(10,14,10,0.97)",
        borderTop: `1px solid ${colors.borderDefault}`,
        padding: "14px 18px",
        zIndex: 9999,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        fontFamily: "'Inter', system-ui, sans-serif",
        boxShadow: "0 -4px 12px rgba(0,0,0,0.35)",
      }}
    >
      <div style={{
        flex: "1 1 320px",
        fontSize: fontSizes.sm,
        color: colors.textMuted,
        lineHeight: 1.5,
        minWidth: 0,
      }}>
        We store a small amount of data in your browser so you stay logged in.
        We do not run advertising trackers. See our{" "}
        <a href="/cookies" style={{ color: colors.goldPrimary, textDecoration: "underline" }}>
          cookie policy
        </a>{" "}
        and{" "}
        <a href="/privacy" style={{ color: colors.goldPrimary, textDecoration: "underline" }}>
          privacy policy
        </a>.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={essentialOnly}
          style={{
            padding: "0.5rem 0.95rem",
            fontSize: fontSizes.sm,
            borderRadius: 4,
            background: "transparent",
            border: `1px solid ${colors.borderDefault}`,
            color: colors.textMuted,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Essential only
        </button>
        <button
          onClick={acceptAll}
          style={{
            padding: "0.5rem 0.95rem",
            fontSize: fontSizes.sm,
            borderRadius: 4,
            background: "rgba(95,166,95,0.85)",
            border: "1px solid rgba(95,166,95,0.6)",
            color: "#fff",
            cursor: "pointer",
            fontWeight: 600,
            fontFamily: "inherit",
          }}
        >
          Accept all
        </button>
      </div>
    </div>
  );
}
